import { useEffect, useState } from "react";
import Ring from "@/components/Ring";

/**
 * The landing hero: the Ring, live, with one line of real transcribed speech
 * underneath. Not a screenshot, not a device mockup — the product doing its
 * actual trick in the first three seconds.
 */
const SCRIPT = [
  { state: "idle", ms: 1600, said: "", heard: "" },
  { state: "listening", ms: 2600, said: "remind me to call Priya before the Thursday sync", heard: "" },
  { state: "thinking", ms: 1400, said: "remind me to call Priya before the Thursday sync", heard: "" },
  {
    state: "speaking",
    ms: 4200,
    said: "remind me to call Priya before the Thursday sync",
    heard: "Held for Wednesday evening, so it doesn't arrive as a surprise.",
  },
];

export function HeroRing() {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setI((n) => (n + 1) % SCRIPT.length), SCRIPT[i].ms);
    return () => clearTimeout(t);
  }, [i]);

  const step = SCRIPT[i];
  // Fake but damped amplitude so the outer ring moves like speech, not noise.
  const amp = step.state === "listening" ? 0.35 + 0.4 * Math.abs(Math.sin(Date.now() / 260)) : 0;

  return (
    <div className="flex flex-col items-center" data-testid="hero-ring-block">
      <Ring
        state={step.state}
        size={112}
        amplitude={amp}
        testId="hero-ring"
        ariaLabel="Rammy"
      />

      <h1
        className="mt-8 font-display max-w-3xl mx-auto"
        style={{
          fontSize: "clamp(2.375rem, 7vw, var(--rm-text-5xl))",
          lineHeight: 1.0,
          letterSpacing: "var(--rm-tracking-display)",
          fontWeight: 700,
          color: "var(--rm-text)",
        }}
        data-testid="hero-headline"
      >
        Everything you tell it,<br />it acts on.
      </h1>

      {/* The one sentence that says what this is. A hero without it makes the
          visitor guess, and they leave instead of guessing. */}
      <p
        className="mt-5 max-w-2xl mx-auto text-base md:text-lg leading-relaxed"
        style={{ color: "var(--rm-text-sec, var(--rammy-text-sec))" }}
        data-testid="hero-subheadline"
      >
        Not a chat window. Rammy remembers your life, books real time in your day,
        and keeps the promises you make — once your calendar and inbox are connected.
        Before that, it still helps.
      </p>

      {/* Proof, not adjectives — and only things that work today. */}
      <ul
        className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px]"
        style={{ color: "var(--rm-text-muted, var(--rammy-text-muted))" }}
        data-testid="hero-proof-row"
      >
        {[
          "Moves real meetings in your calendar",
          "Drafts the reply, you approve the send",
          "Catches the promise you made in passing",
          "Never metered on a free account",
        ].map((p) => (
          <li key={p} className="inline-flex items-center gap-1.5">
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: "var(--rammy-cyan-dark)" }}
              aria-hidden="true"
            />
            {p}
          </li>
        ))}
      </ul>

      {/* One line of real transcribed speech. The user's words in the
          interface sans; Rammy's answer in the serif. */}
      <div className="mt-8 min-h-[104px] w-full max-w-xl" data-testid="hero-transcript">
        {step.said && (
          <p
            className="text-center"
            style={{
              fontSize: "var(--rm-text-base)",
              color: "var(--rm-text-muted)",
              fontFamily: "var(--rm-font-ui)",
            }}
            data-testid="hero-transcript-user"
          >
            “{step.said}”
          </p>
        )}
        {step.heard && (
          <p
            className="rm-voice mt-3 mx-auto text-center fade-up"
            data-testid="hero-transcript-rammy"
          >
            {step.heard}
          </p>
        )}
      </div>
    </div>
  );
}

export default HeroRing;
